/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useState } from 'react';
import type { UserPreferencesDto } from '@shared/types';
import { fetchPreferences, savePreferences } from '../services/formularyApi';
import { PageHelp } from '../components/layout/PageHelp';
import { Tooltip } from '../components/layout/Tooltip';

export function SettingsPage() {
  const [prefs, setPrefs] = useState<UserPreferencesDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchPreferences().then((r) => {
      setPrefs(r.data);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, []);

  const handleSave = async () => {
    if (!prefs) return;
    setIsSaving(true);
    setMessage(null);
    try {
      await savePreferences(prefs);
      setMessage('Preferences saved');
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !prefs) {
    return <div className="text-center text-gray-400 py-12">Loading preferences...</div>;
  }

  return (
    <div className="max-w-md">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Settings</h2>

      <PageHelp
        title="About Settings"
        steps={[
          'Adjust your preferences below — changes take effect after clicking Save.',
          'Page Size controls how many formulas appear per page in the Formula Queue.',
          'Startup View determines which page loads when you first log in.',
          'Claim Batch Size controls how many formulas are claimed at once.',
        ]}
        tips={[
          'Settings are saved per user and persist across sessions.',
          'A larger page size shows more formulas but may load slower.',
        ]}
      />

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        {/* Page size */}
        <div>
          <Tooltip text="Number of formulas displayed per page in the Formula Queue">
            <label className="block text-sm font-medium text-gray-700 mb-1 cursor-help">Default Page Size</label>
          </Tooltip>
          <select
            value={prefs.pageSize}
            onChange={(e) => setPrefs({ ...prefs, pageSize: Number(e.target.value) as 25 | 50 | 100 })}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Startup view */}
        <div>
          <Tooltip text="The page shown when you first log in to DataMave Formulary">
            <label className="block text-sm font-medium text-gray-700 mb-1 cursor-help">Startup View</label>
          </Tooltip>
          <select
            value={prefs.startupView}
            onChange={(e) => setPrefs({ ...prefs, startupView: e.target.value as 'FormulaQueue' | 'Dashboard' })}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="FormulaQueue">Formula Queue</option>
            <option value="Dashboard">Dashboard</option>
          </select>
        </div>

        {/* Claim batch size */}
        <div>
          <Tooltip text="How many formulas to claim at once when using the claim feature">
            <label className="block text-sm font-medium text-gray-700 mb-1 cursor-help">Claim Batch Size</label>
          </Tooltip>
          <input
            type="number"
            min={1}
            max={50}
            value={prefs.claimBatchSize}
            onChange={(e) => setPrefs({ ...prefs, claimBatchSize: Math.min(50, Math.max(1, parseInt(e.target.value, 10) || 10)) })}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">1–50 formulas per claim batch</p>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-dds-blue-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-dds-blue-600 disabled:opacity-50 w-full"
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </button>

        {message && (
          <p className={`text-sm text-center ${message.includes('Failed') ? 'text-red-500' : 'text-green-600'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
